import { TruckIcon, UserIcon } from '@heroicons/react/24/outline';

import { type EvidenceData } from '@/components/citations';
import {
  buildFullName,
  EntityCard,
  EntityCardHeader,
  EntityCardSection,
  EntityDisclosure,
  formatEnum,
} from '@/components/shared';
import { FieldWithApproval } from '@/components/suggestions';
import type {
  ReadVehicle,
  ReadVehicleWithOccupants,
} from '@/services/api/subjectsService';

interface VehicleCardProps {
  vehicle: ReadVehicle | ReadVehicleWithOccupants;
  showOccupants?: boolean;
  onEdit?: () => void;
  onApproveField?: (fieldKey: string) => void;
  showApproval?: boolean;
  evidence?: EvidenceData[];
  evidenceTitle?: string;
  matterId?: string;
}

export const VehicleCard = ({
  vehicle,
  showOccupants = false,
  onEdit,
  onApproveField,
  showApproval = false,
  evidence,
  evidenceTitle,
  matterId,
}: VehicleCardProps) => {
  const displayName = [vehicle.year, vehicle.make, vehicle.model]
    .filter(Boolean)
    .join(' ');

  const hasOccupants =
    showOccupants &&
    'occupants' in vehicle &&
    vehicle.occupants &&
    vehicle.occupants.length > 0;
  const driver =
    hasOccupants &&
    (vehicle as ReadVehicleWithOccupants).occupants?.find(
      (occ) => occ.role === 'driver',
    );
  const passengers =
    hasOccupants &&
    (vehicle as ReadVehicleWithOccupants).occupants?.filter(
      (occ) => occ.role === 'passenger',
    );

  const driverName = driver
    ? buildFullName(
        driver.person.first_name,
        undefined,
        driver.person.last_name,
      ) || driver.person.short_description
    : null;

  const hasAdditionalDetails =
    vehicle.vin ||
    vehicle.estimated_speed_mph ||
    vehicle.license_plate ||
    vehicle.color;

  return (
    <EntityCard
      icon={<TruckIcon />}
      approvalMetadata={vehicle.approval_metadata}
      showApproval={showApproval}
      onEdit={onEdit}
      evidence={evidence}
      evidenceTitle={evidenceTitle}
      matterId={matterId}
    >
      <EntityCardHeader title={displayName || 'Vehicle'} />

      {vehicle.involvement_description && (
        <p className="mt-1 text-sm text-gray-600">
          {vehicle.involvement_description}
        </p>
      )}

      <div className="mt-1 flex items-center gap-2 text-sm">
        {vehicle.vehicle_type && !showApproval && (
          <span className="text-gray-500 capitalize">
            {formatEnum(vehicle.vehicle_type)}
          </span>
        )}
        {vehicle.color && !showApproval && vehicle.vehicle_type && (
          <span className="text-gray-400">·</span>
        )}
        {vehicle.color && !showApproval && (
          <span className="text-gray-500">{vehicle.color}</span>
        )}
      </div>

      {/* License plate */}
      {vehicle.license_plate && !showApproval && (
        <EntityCardSection className="mt-2 text-sm text-gray-900">
          {vehicle.license_plate}
          {vehicle.license_plate_state_code && (
            <span className="text-gray-500">
              {' '}
              · {vehicle.license_plate_state_code}
            </span>
          )}
        </EntityCardSection>
      )}

      {/* Occupants */}
      {hasOccupants && (
        <EntityCardSection className="mt-2 space-y-1.5">
          {driverName && (
            <div className="flex items-center gap-1.5 text-sm">
              <UserIcon className="h-4 w-4 text-blue-600" />
              <div>
                <span className="text-xs text-gray-500">Driver · </span>
                <span className="font-medium text-blue-900">{driverName}</span>
              </div>
            </div>
          )}
          {passengers && passengers.length > 0 && (
            <>
              {passengers.map((p) => {
                const passengerName = buildFullName(
                  p.person.first_name,
                  undefined,
                  p.person.last_name,
                );
                return (
                  <div key={p.id} className="flex items-center gap-1.5 text-sm">
                    <UserIcon className="h-4 w-4 text-gray-400" />
                    <div>
                      <span className="text-xs text-gray-500">
                        Passenger ·{' '}
                      </span>
                      <span className="text-gray-900">
                        {passengerName ||
                          p.person.short_description ||
                          'Unnamed'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </EntityCardSection>
      )}

      {/* Details disclosure */}
      {hasAdditionalDetails && (
        <EntityDisclosure>
          {showApproval && onApproveField ? (
            <dl className="space-y-2">
              <FieldWithApproval
                label="License Plate"
                value={
                  vehicle.license_plate
                    ? `${vehicle.license_plate}${
                        vehicle.license_plate_state_code
                          ? ` · ${vehicle.license_plate_state_code}`
                          : ''
                      }`
                    : null
                }
                fieldKey="license_plate"
                approvalMetadata={vehicle.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Vehicle Type"
                value={vehicle.vehicle_type && formatEnum(vehicle.vehicle_type)}
                fieldKey="vehicle_type"
                approvalMetadata={vehicle.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Color"
                value={vehicle.color}
                fieldKey="color"
                approvalMetadata={vehicle.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="VIN"
                value={vehicle.vin}
                fieldKey="vin"
                approvalMetadata={vehicle.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
              <FieldWithApproval
                label="Estimated Speed"
                value={
                  vehicle.estimated_speed_mph
                    ? `${vehicle.estimated_speed_mph} mph`
                    : null
                }
                fieldKey="estimated_speed_mph"
                approvalMetadata={vehicle.approval_metadata}
                onApprove={onApproveField}
                showApproveButton
              />
            </dl>
          ) : (
            <>
              {vehicle.license_plate && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    License Plate
                  </div>
                  <div className="text-sm text-gray-900">
                    {vehicle.license_plate}
                    {vehicle.license_plate_state_code &&
                      ` · ${vehicle.license_plate_state_code}`}
                  </div>
                </div>
              )}

              {vehicle.vin && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    VIN
                  </div>
                  <div className="text-sm text-gray-900">{vehicle.vin}</div>
                </div>
              )}

              {vehicle.estimated_speed_mph && (
                <div className="space-y-1">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Estimated Speed
                  </div>
                  <div className="text-sm text-gray-900">
                    {vehicle.estimated_speed_mph} mph
                  </div>
                </div>
              )}
            </>
          )}
        </EntityDisclosure>
      )}
    </EntityCard>
  );
};
